import React, { useState, useEffect } from "react";

const LoginSignup = () => {
  // Existing login states…
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // New state to decide which form to show
  const [isReset, setIsReset] = useState(false);

  // State for reset password form
  const [resetEmail, setResetEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  useEffect(() => {
    // Check if the URL contains resetPassword parameter
    const params = new URLSearchParams(window.location.search);
    setIsReset(params.has("resetPassword"));
  }, []);

  // Existing login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!email || !password) {
      setError("Please fill in all fields");
      setLoading(false);
      return;
    }

    const requestData = { username: email, password, remember_me: rememberMe };

    try {
      const response = await fetch(`${dash_ajax.rest_url}login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": dash_ajax.nonce,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`Error: Invalid username or password`);
      }

      const result = await response.json();
      if (result.redirect_url) {
        window.location.href = result.redirect_url;
      } else {
        setError("Login successful, but no redirect URL provided");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // New reset password handler
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetMessage("");

    // Basic client-side validation
    if (!resetEmail || !newPassword || !confirmPassword) {
      setResetMessage("Please fill in all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetMessage("Passwords do not match.");
      return;
    }

    // Prepare request data with identifier (email/username) and new password fields
    const requestData = {
      username: resetEmail, // This field can accept either email or username
      new_password: newPassword,
      confirm_password: confirmPassword,
    };

    try {
      const response = await fetch(`${dash_ajax.rest_url}reset-password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": dash_ajax.nonce,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        setResetMessage("Password reset successful. You may now log in.");
        // Optionally redirect to the login form:
        // window.location.href = '/login';
      } else {
        setResetMessage(result.message || "Password reset failed.");
      }
    } catch (error) {
      setResetMessage(error.message);
    }
  };

  const handleShowReset = (e) => {
    e.preventDefault();
    setIsReset(true);
    // Optionally, update the URL to reflect the state
    const params = new URLSearchParams(window.location.search);
    params.set("resetPassword", "1");
    window.history.replaceState(null, "", "?" + params.toString());
  };

  const handleShowLogin = (e) => {
    e.preventDefault();
    setIsReset(false);
    // Remove the "resetPassword" parameter from the URL
    const params = new URLSearchParams(window.location.search);
    params.delete("resetPassword");
    window.history.replaceState(null, "", "?" + params.toString());
  };

  return (
    <div className="row justify-content-center">
      <div className="col-md-6">
        {isReset ? (
          <>
            <h2>Reset Password</h2>
            {resetMessage && (
              <div className="alert alert-info">{resetMessage}</div>
            )}
            <form onSubmit={handleResetPassword}>
              <div className="mb-3">
                <label htmlFor="resetEmail" className="form-label">
                  Email address / username
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="newPassword" className="form-label">
                  New Password
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary">
                Reset Password
              </button>
              <div className="mt-3">
                <a href="#" onClick={handleShowLogin}>
                  Login here
                </a>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2>Login</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email address / username
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="rememberMe">
                  Remember me
                </label>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? "Logging in..." : "Login"}
              </button>
              <div className="mt-3">
                <a href="#" onClick={handleShowReset}>
                  Forgot Password?
                </a>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginSignup;
